import React, { useState, useEffect, useCallback } from 'react';
import {
  FaTimes,
  FaSave,
  FaCheckCircle,
  FaCog,
  FaUpload,
  FaEnvelope,
  FaSlack,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaGoogle,
  FaShoppingCart,
  FaLink,
  FaPlay,
  FaFileAlt,
  FaSync
} from 'react-icons/fa';

const NodeSettingsPanel = ({ node, onClose, onSave, isVisible, serviceConnections = {} }) => {
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('config');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionDetails, setConnectionDetails] = useState({});

  const getConnectionKey = useCallback((nodeType) => {
    const connectionMap = {
      'trigger-email-gmail': 'gmail',
      'action-email': 'gmail',
      'trigger-slack-message': 'slack',
      'action-slack': 'slack',
      'trigger-instagram-post': 'instagram',
      'action-instagram-post': 'instagram',
      'trigger-facebook-page': 'facebook',
      'action-facebook-post': 'facebook',
      'trigger-twitter-mention': 'twitter',
      'action-twitter-post': 'twitter',
      'trigger-youtube-upload': 'youtube',
      'action-youtube-upload': 'youtube',
      'action-linkedin-post': 'linkedin',
      'trigger-google-sheets': 'google',
      'action-google-sheets': 'google',
      'action-google-drive': 'google',
      'action-google-calendar': 'google',
      'action-google-analytics': 'google',
      'trigger-shopify-order': 'shopify',
      'action-shopify-product': 'shopify',
      'trigger-square-payment': 'square',
      'action-square-invoice': 'square',
      'action-toast-pos': 'toast',
      // Add more mappings as needed
    };
    return connectionMap[nodeType] || null;
  }, []);

  const getConnectionDetails = useCallback((nodeType) => {
    const details = {
      'gmail': {
        name: 'Gmail',
        icon: FaEnvelope,
        scopes: ['gmail.readonly', 'gmail.send'],
        permissions: ['Read emails', 'Send emails', 'Manage labels'],
        testEndpoint: 'https://gmail.googleapis.com/gmail/v1/users/me/profile'
      },
      'slack': {
        name: 'Slack',
        icon: FaSlack,
        scopes: ['channels:read', 'chat:write', 'users:read'],
        permissions: ['Read channels', 'Send messages', 'Access user info'],
        testEndpoint: 'https://slack.com/api/auth.test'
      },
      'instagram': {
        name: 'Instagram',
        icon: FaInstagram,
        scopes: ['instagram_basic', 'instagram_content_publish'],
        permissions: ['Basic profile access', 'Publish content', 'Read media'],
        testEndpoint: 'https://graph.instagram.com/me'
      },
      'facebook': {
        name: 'Facebook',
        icon: FaFacebook,
        scopes: ['pages_read_engagement', 'pages_manage_posts'],
        permissions: ['Read page data', 'Manage posts', 'Access insights'],
        testEndpoint: 'https://graph.facebook.com/me/accounts'
      },
      'twitter': {
        name: 'Twitter',
        icon: FaTwitter,
        scopes: ['tweet.read', 'tweet.write', 'users.read'],
        permissions: ['Read tweets', 'Post tweets', 'Access user data'],
        testEndpoint: 'https://api.twitter.com/2/users/me'
      },
      'youtube': {
        name: 'YouTube',
        icon: FaYoutube,
        scopes: ['youtube.upload', 'youtube.readonly'],
        permissions: ['Upload videos', 'Read channel data', 'Manage playlists'],
        testEndpoint: 'https://www.googleapis.com/youtube/v3/channels'
      },
      'google': {
        name: 'Google Workspace',
        icon: FaGoogle,
        scopes: ['sheets', 'drive', 'calendar'],
        permissions: ['Access Google Sheets', 'Manage Drive files', 'Calendar access'],
        testEndpoint: 'https://www.googleapis.com/oauth2/v1/userinfo'
      },
      'shopify': {
        name: 'Shopify',
        icon: FaShoppingCart,
        scopes: ['read_orders', 'write_products'],
        permissions: ['Read orders', 'Manage products', 'Access customer data'],
        testEndpoint: 'https://{shop}.myshopify.com/admin/api/2023-01/orders.json'
      }
    };
    
    const connectionKey = getConnectionKey(nodeType);
    return details[connectionKey] || null;
  }, [getConnectionKey]);

  useEffect(() => {
    if (node) {
      setSettings(node.data?.configuration || {});

      // Check if this node type requires a connection and if it's connected
      if (node.data?.requiresConnection) {
        const connectionKey = getConnectionKey(node.data.type);
        setIsConnected(serviceConnections[connectionKey] || false);
        setConnectionDetails(getConnectionDetails(node.data.type));
      }
    }
  }, [node, serviceConnections, getConnectionKey, getConnectionDetails]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, settings);
    }
    onClose();
  };

  const handleConnect = () => {
    const connectionKey = getConnectionKey(node.data?.type);
    if (connectionKey) {
      // Trigger OAuth flow for the specific service
      window.open(`/auth/${connectionKey}`, '_blank', 'width=600,height=600');
    }
  };

  const testConnection = async () => {
    const details = getConnectionDetails(node.data?.type);
    if (details && details.testEndpoint) {
      try {
        // This would make an actual API call to test the connection
        console.log(`Testing connection to ${details.name}...`);
        // Simulate connection test
        setTimeout(() => {
          alert(`✅ Connection to ${details.name} is working!`);
        }, 1000);
      } catch (error) {
        alert(`❌ Connection test failed: ${error.message}`);
      }
    }
  };

  if (!isVisible || !node) {
    return null;
  }

  const renderTabButton = (tabId, label, icon) => (
    <button
      className={`tab-btn ${activeTab === tabId ? 'active' : ''}`}
      onClick={() => setActiveTab(tabId)}
    >
      {icon}
      {label}
    </button>
  );

  const renderConnectionStatus = () => {
    const connectionKey = getConnectionKey(node.data?.type);
    if (!connectionKey) return null;

    const details = connectionDetails;
    if (!details) return null;

    const IconComponent = details.icon;

    return (
      <div className="connection-section">
        <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
          <div className="connection-header">
            <div className="service-info">
              <IconComponent className="service-icon" />
              <div>
                <h4>{details.name}</h4>
                <span className={`status-text ${isConnected ? 'connected' : 'disconnected'}`}>
                  {isConnected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
            </div>
            <div className="connection-actions">
              {isConnected ? (
                <>
                  <button className="btn secondary small" onClick={testConnection}>
                    <FaSync /> Test
                  </button>
                  <button className="btn danger small">
                    <FaTimes /> Disconnect
                  </button>
                </>
              ) : (
                <button className="btn primary small" onClick={handleConnect}>
                  <FaLink /> Connect
                </button>
              )}
            </div>
          </div>
        </div>

        {isConnected && (
          <div className="connection-details">
            <div className="permissions-list">
              <h5>Permissions</h5>
              {details.permissions.map((permission, index) => (
                <div key={index} className="permission-item">
                  <FaCheckCircle className="permission-icon" />
                  {permission}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderNodeSpecificSettings = () => {
    const nodeType = node.data?.type || '';
    
    switch (nodeType) {
      // === TRIGGERS ===
      case 'trigger-email-gmail':
        return (
          <>
            <div className="setting-field">
              <label>Email Filter</label>
              <select value={settings.filterType || 'all'} onChange={(e) => handleSettingChange('filterType', e.target.value)}>
                <option value="all">All Emails</option>
                <option value="unread">Unread Only</option>
                <option value="from">From Specific Sender</option>
                <option value="subject">Subject Contains</option>
                <option value="label">Has Label</option>
              </select>
            </div>
            {settings.filterType === 'from' && (
              <div className="setting-field">
                <label>Sender Email</label>
                <input type="email" value={settings.senderEmail || ''} onChange={(e) => handleSettingChange('senderEmail', e.target.value)} placeholder="sender@example.com" />
              </div>
            )}
            {settings.filterType === 'subject' && (
              <div className="setting-field">
                <label>Subject Keywords</label>
                <input type="text" value={settings.subjectKeywords || ''} onChange={(e) => handleSettingChange('subjectKeywords', e.target.value)} placeholder="urgent, invoice, order" />
              </div>
            )}
            {settings.filterType === 'label' && (
              <div className="setting-field">
                <label>Gmail Label</label>
                <input type="text" value={settings.gmailLabel || ''} onChange={(e) => handleSettingChange('gmailLabel', e.target.value)} placeholder="Important, Work, etc." />
              </div>
            )}
            <div className="setting-field">
              <label>Check Frequency</label>
              <select value={settings.frequency || '5min'} onChange={(e) => handleSettingChange('frequency', e.target.value)}>
                <option value="1min">Every 1 minute</option>
                <option value="5min">Every 5 minutes</option>
                <option value="15min">Every 15 minutes</option>
                <option value="1hour">Every hour</option>
              </select>
            </div>
          </>
        );

      case 'trigger-slack-message':
        return (
          <>
            <div className="setting-field">
              <label>Channel/DM</label>
              <select value={settings.channelType || 'channel'} onChange={(e) => handleSettingChange('channelType', e.target.value)}>
                <option value="channel">Specific Channel</option>
                <option value="dm">Direct Messages</option>
                <option value="mention">Mentions Only</option>
                <option value="all">All Messages</option>
              </select>
            </div>
            {settings.channelType === 'channel' && (
              <div className="setting-field">
                <label>Channel Name</label>
                <input type="text" value={settings.channelName || ''} onChange={(e) => handleSettingChange('channelName', e.target.value)} placeholder="#general, #alerts" />
              </div>
            )}
            <div className="setting-field">
              <label>Message Filter</label>
              <input type="text" value={settings.messageFilter || ''} onChange={(e) => handleSettingChange('messageFilter', e.target.value)} placeholder="Keywords to filter by" />
            </div>
            <div className="setting-field">
              <label>Include Bot Messages</label>
              <select value={settings.includeBots || 'false'} onChange={(e) => handleSettingChange('includeBots', e.target.value)}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </>
        );

      case 'trigger-schedule':
        return (
          <>
            <div className="setting-field">
              <label>Schedule Type</label>
              <select value={settings.scheduleType || 'interval'} onChange={(e) => handleSettingChange('scheduleType', e.target.value)}>
                <option value="interval">Interval</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="cron">Custom Cron</option>
              </select>
            </div>
            
            {settings.scheduleType === 'interval' && (
              <>
                <div className="setting-field">
                  <label>Interval</label>
                  <div className="input-group">
                    <input type="number" value={settings.intervalValue || 60} onChange={(e) => handleSettingChange('intervalValue', parseInt(e.target.value))} min="1" />
                    <select value={settings.intervalUnit || 'minutes'} onChange={(e) => handleSettingChange('intervalUnit', e.target.value)}>
                      <option value="seconds">Seconds</option>
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {settings.scheduleType === 'daily' && (
              <div className="setting-field">
                <label>Time</label>
                <input type="time" value={settings.time || '09:00'} onChange={(e) => handleSettingChange('time', e.target.value)} />
              </div>
            )}

            {settings.scheduleType === 'weekly' && (
              <>
                <div className="setting-field">
                  <label>Day of Week</label>
                  <select value={settings.dayOfWeek || 'monday'} onChange={(e) => handleSettingChange('dayOfWeek', e.target.value)}>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
                <div className="setting-field">
                  <label>Time</label>
                  <input type="time" value={settings.time || '09:00'} onChange={(e) => handleSettingChange('time', e.target.value)} />
                </div>
              </>
            )}

            {settings.scheduleType === 'cron' && (
              <div className="setting-field">
                <label>Cron Expression</label>
                <input type="text" value={settings.cronExpression || '0 9 * * *'} onChange={(e) => handleSettingChange('cronExpression', e.target.value)} placeholder="0 9 * * * (9 AM daily)" />
                <small>Use cron format: minute hour day month weekday</small>
              </div>
            )}

            <div className="setting-field">
              <label>Timezone</label>
              <select value={settings.timezone || 'America/New_York'} onChange={(e) => handleSettingChange('timezone', e.target.value)}>
                <option value="America/New_York">Eastern Time (EST/EDT)</option>
                <option value="America/Chicago">Central Time (CST/CDT)</option>
                <option value="America/Denver">Mountain Time (MST/MDT)</option>
                <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                <option value="UTC">UTC</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
              </select>
            </div>
          </>
        );

      case 'trigger-webhook':
        return (
          <>
            <div className="setting-field">
              <label>Webhook URL</label>
              <div className="input-with-copy">
                <input type="text" value={settings.webhookUrl || `https://hooks.broaderbusiness.com/${node.id}`} readOnly />
                <button className="btn copy-btn" onClick={() => navigator.clipboard.writeText(settings.webhookUrl)}>
                  <FaLink />
                </button>
              </div>
              <small>This URL will receive HTTP requests to trigger the workflow</small>
            </div>
            <div className="setting-field">
              <label>Allowed Methods</label>
              <div className="checkbox-group">
                {['GET', 'POST', 'PUT', 'DELETE'].map(method => (
                  <label key={method} className="checkbox-item">
                    <input 
                      type="checkbox" 
                      checked={settings.allowedMethods?.includes(method) || method === 'POST'}
                      onChange={(e) => {
                        const methods = settings.allowedMethods || ['POST'];
                        if (e.target.checked) {
                          handleSettingChange('allowedMethods', [...methods, method]);
                        } else {
                          handleSettingChange('allowedMethods', methods.filter(m => m !== method));
                        }
                      }}
                    />
                    {method}
                  </label>
                ))}
              </div>
            </div>
            <div className="setting-field">
              <label>Authentication</label>
              <select value={settings.authType || 'none'} onChange={(e) => handleSettingChange('authType', e.target.value)}>
                <option value="none">None</option>
                <option value="token">Bearer Token</option>
                <option value="basic">Basic Auth</option>
                <option value="signature">Webhook Signature</option>
              </select>
            </div>
            {settings.authType === 'token' && (
              <div className="setting-field">
                <label>Bearer Token</label>
                <input type="password" value={settings.bearerToken || ''} onChange={(e) => handleSettingChange('bearerToken', e.target.value)} placeholder="your-secret-token" />
              </div>
            )}
          </>
        );

      // === ACTIONS ===
      case 'action-email':
        return (
          <>
            <div className="setting-field">
              <label>Email Provider</label>
              <select value={settings.provider || 'gmail'} onChange={(e) => handleSettingChange('provider', e.target.value)}>
                <option value="gmail">Gmail</option>
                <option value="outlook">Outlook</option>
                <option value="smtp">Custom SMTP</option>
              </select>
            </div>
            <div className="setting-field">
              <label>To Email</label>
              <input type="email" value={settings.to || ''} onChange={(e) => handleSettingChange('to', e.target.value)} placeholder="recipient@example.com" />
            </div>
            <div className="setting-field">
              <label>CC (Optional)</label>
              <input type="email" value={settings.cc || ''} onChange={(e) => handleSettingChange('cc', e.target.value)} placeholder="cc@example.com" />
            </div>
            <div className="setting-field">
              <label>Subject</label>
              <input type="text" value={settings.subject || ''} onChange={(e) => handleSettingChange('subject', e.target.value)} placeholder="Email subject" />
            </div>
            <div className="setting-field">
              <label>Email Template</label>
              <select value={settings.template || 'custom'} onChange={(e) => handleSettingChange('template', e.target.value)}>
                <option value="custom">Custom Message</option>
                <option value="welcome">Welcome Email</option>
                <option value="notification">Notification</option>
                <option value="invoice">Invoice</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Message</label>
              <textarea 
                rows="6" 
                value={settings.message || ''} 
                onChange={(e) => handleSettingChange('message', e.target.value)} 
                placeholder="Email content..."
              />
              <small>You can use variables like {`{{name}}`}, {`{{email}}`}, {`{{date}}`} in your message</small>
            </div>
            <div className="setting-field">
              <label>Attachments</label>
              <div className="attachment-list">
                <button className="btn secondary">
                  <FaUpload /> Add Attachment
                </button>
              </div>
            </div>
          </>
        );

      case 'action-instagram-post':
        return (
          <>
            <div className="setting-field">
              <label>Post Type</label>
              <select value={settings.postType || 'image'} onChange={(e) => handleSettingChange('postType', e.target.value)}>
                <option value="image">Image</option>
                <option value="carousel">Carousel</option>
                <option value="video">Video</option>
                <option value="reel">Reel</option>
                <option value="story">Story</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Caption</label>
              <textarea 
                rows="4" 
                value={settings.caption || ''} 
                onChange={(e) => handleSettingChange('caption', e.target.value)} 
                placeholder="What's happening..."
                maxLength="2200"
              />
              <small>{(settings.caption || '').length}/2200 characters</small>
            </div>
            <div className="setting-field">
              <label>Hashtags</label>
              <input 
                type="text" 
                value={settings.hashtags || ''} 
                onChange={(e) => handleSettingChange('hashtags', e.target.value)} 
                placeholder="#business #marketing #growth"
              />
            </div>
            <div className="setting-field">
              <label>Media</label>
              <div className="media-upload">
                <button className="btn upload-btn">
                  <FaUpload /> Upload {settings.postType || 'Image'}
                </button>
                <small>Supported: JPG, PNG, MP4 (max 100MB)</small>
              </div>
            </div>
            <div className="setting-field">
              <label>Post Timing</label>
              <select value={settings.timing || 'now'} onChange={(e) => handleSettingChange('timing', e.target.value)}>
                <option value="now">Post Immediately</option>
                <option value="optimal">Optimal Time</option>
                <option value="scheduled">Schedule for Later</option>
              </select>
            </div>
            {settings.timing === 'scheduled' && (
              <div className="setting-field">
                <label>Schedule Date & Time</label>
                <input 
                  type="datetime-local" 
                  value={settings.scheduledTime || ''} 
                  onChange={(e) => handleSettingChange('scheduledTime', e.target.value)} 
                />
              </div>
            )}
            <div className="setting-field">
              <label>Location (Optional)</label>
              <input 
                type="text" 
                value={settings.location || ''} 
                onChange={(e) => handleSettingChange('location', e.target.value)} 
                placeholder="City, State"
              />
            </div>
          </>
        );

      case 'action-facebook-post':
        return (
          <>
            <div className="setting-field">
              <label>Facebook Page</label>
              <select value={settings.pageId || ''} onChange={(e) => handleSettingChange('pageId', e.target.value)}>
                <option value="">Select a page...</option>
                <option value="page1">Your Business Page</option>
                <option value="page2">Secondary Page</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Post Type</label>
              <select value={settings.postType || 'text'} onChange={(e) => handleSettingChange('postType', e.target.value)}>
                <option value="text">Text Only</option>
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="link">Link Share</option>
                <option value="event">Event</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Message</label>
              <textarea 
                rows="4" 
                value={settings.message || ''} 
                onChange={(e) => handleSettingChange('message', e.target.value)} 
                placeholder="What's on your mind..."
                maxLength="63206"
              />
            </div>
            {settings.postType === 'link' && (
              <>
                <div className="setting-field">
                  <label>Link URL</label>
                  <input type="url" value={settings.linkUrl || ''} onChange={(e) => handleSettingChange('linkUrl', e.target.value)} placeholder="https://example.com" />
                </div>
                <div className="setting-field">
                  <label>Link Description</label>
                  <input type="text" value={settings.linkDescription || ''} onChange={(e) => handleSettingChange('linkDescription', e.target.value)} placeholder="Brief description of the link" />
                </div>
              </>
            )}
            <div className="setting-field">
              <label>Audience</label>
              <select value={settings.audience || 'public'} onChange={(e) => handleSettingChange('audience', e.target.value)}>
                <option value="public">Public</option>
                <option value="friends">Friends Only</option>
                <option value="custom">Custom Audience</option>
              </select>
            </div>
          </>
        );

      case 'action-slack':
        return (
          <>
            <div className="setting-field">
              <label>Destination</label>
              <select value={settings.destination || 'channel'} onChange={(e) => handleSettingChange('destination', e.target.value)}>
                <option value="channel">Channel</option>
                <option value="user">Direct Message</option>
                <option value="webhook">Webhook URL</option>
              </select>
            </div>
            {settings.destination === 'channel' && (
              <div className="setting-field">
                <label>Channel</label>
                <select value={settings.channel || ''} onChange={(e) => handleSettingChange('channel', e.target.value)}>
                  <option value="">Select channel...</option>
                  <option value="general">#general</option>
                  <option value="alerts">#alerts</option>
                  <option value="team">#team</option>
                </select>
              </div>
            )}
            {settings.destination === 'user' && (
              <div className="setting-field">
                <label>User</label>
                <input type="text" value={settings.user || ''} onChange={(e) => handleSettingChange('user', e.target.value)} placeholder="@username or user ID" />
              </div>
            )}
            <div className="setting-field">
              <label>Message</label>
              <textarea 
                rows="4" 
                value={settings.message || ''} 
                onChange={(e) => handleSettingChange('message', e.target.value)} 
                placeholder="Message content..."
              />
              <small>Supports Slack markdown formatting</small>
            </div>
            <div className="setting-field">
              <label>Bot Name (Optional)</label>
              <input type="text" value={settings.botName || ''} onChange={(e) => handleSettingChange('botName', e.target.value)} placeholder="Custom bot name" />
            </div>
            <div className="setting-field">
              <label>Bot Icon (Optional)</label>
              <input type="text" value={settings.botIcon || ''} onChange={(e) => handleSettingChange('botIcon', e.target.value)} placeholder=":robot_face: or emoji" />
            </div>
            <div className="setting-field">
              <label>Thread Reply</label>
              <select value={settings.threadReply || 'false'} onChange={(e) => handleSettingChange('threadReply', e.target.value)}>
                <option value="false">New Message</option>
                <option value="true">Reply in Thread</option>
              </select>
            </div>
          </>
        );

      case 'action-google-sheets':
        return (
          <>
            <div className="setting-field">
              <label>Spreadsheet</label>
              <select value={settings.spreadsheetId || ''} onChange={(e) => handleSettingChange('spreadsheetId', e.target.value)}>
                <option value="">Select spreadsheet...</option>
                <option value="sheet1">Customer Data</option>
                <option value="sheet2">Sales Tracking</option>
                <option value="sheet3">Marketing Metrics</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Worksheet</label>
              <input type="text" value={settings.worksheet || 'Sheet1'} onChange={(e) => handleSettingChange('worksheet', e.target.value)} placeholder="Sheet1" />
            </div>
            <div className="setting-field">
              <label>Action</label>
              <select value={settings.action || 'append'} onChange={(e) => handleSettingChange('action', e.target.value)}>
                <option value="append">Add New Row</option>
                <option value="update">Update Existing Row</option>
                <option value="read">Read Data</option>
                <option value="clear">Clear Range</option>
              </select>
            </div>
            {(settings.action === 'append' || settings.action === 'update') && (
              <div className="setting-field">
                <label>Data Mapping</label>
                <div className="data-mapping">
                  <div className="mapping-row">
                    <input type="text" placeholder="Column A" value={settings.colA || ''} onChange={(e) => handleSettingChange('colA', e.target.value)} />
                    <span>=</span>
                    <input type="text" placeholder="{{data.field}}" value={settings.dataA || ''} onChange={(e) => handleSettingChange('dataA', e.target.value)} />
                  </div>
                  <div className="mapping-row">
                    <input type="text" placeholder="Column B" value={settings.colB || ''} onChange={(e) => handleSettingChange('colB', e.target.value)} />
                    <span>=</span>
                    <input type="text" placeholder="{{data.field}}" value={settings.dataB || ''} onChange={(e) => handleSettingChange('dataB', e.target.value)} />
                  </div>
                  <button className="btn secondary small">+ Add Column</button>
                </div>
              </div>
            )}
          </>
        );

      case 'ai-content-generator':
        return (
          <>
            <div className="setting-field">
              <label>Content Type</label>
              <select value={settings.contentType || 'blog-post'} onChange={(e) => handleSettingChange('contentType', e.target.value)}>
                <option value="blog-post">Blog Post</option>
                <option value="social-post">Social Media Post</option>
                <option value="email">Email Content</option>
                <option value="product-description">Product Description</option>
                <option value="ad-copy">Ad Copy</option>
                <option value="press-release">Press Release</option>
                <option value="newsletter">Newsletter</option>
              </select>
            </div>
            <div className="setting-field">
              <label>AI Model</label>
              <select value={settings.model || 'gpt-4'} onChange={(e) => handleSettingChange('model', e.target.value)}>
                <option value="gpt-4">GPT-4 (Premium)</option>
                <option value="gpt-3.5">GPT-3.5 (Fast)</option>
                <option value="claude">Claude (Balanced)</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Content Prompt</label>
              <textarea 
                rows="4" 
                value={settings.prompt || ''} 
                onChange={(e) => handleSettingChange('prompt', e.target.value)} 
                placeholder="Describe what content you want to generate..."
              />
            </div>
            <div className="setting-field">
              <label>Tone</label>
              <select value={settings.tone || 'professional'} onChange={(e) => handleSettingChange('tone', e.target.value)}>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="creative">Creative</option>
                <option value="humorous">Humorous</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Target Audience</label>
              <input 
                type="text" 
                value={settings.audience || ''} 
                onChange={(e) => handleSettingChange('audience', e.target.value)} 
                placeholder="Small business owners, millennials, etc."
              />
            </div>
            <div className="setting-field">
              <label>Content Length</label>
              <select value={settings.length || 'medium'} onChange={(e) => handleSettingChange('length', e.target.value)}>
                <option value="short">Short (50-150 words)</option>
                <option value="medium">Medium (150-500 words)</option>
                <option value="long">Long (500-1000 words)</option>
                <option value="very-long">Very Long (1000+ words)</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Keywords to Include</label>
              <input 
                type="text" 
                value={settings.keywords || ''} 
                onChange={(e) => handleSettingChange('keywords', e.target.value)} 
                placeholder="SEO, marketing, business growth"
              />
            </div>
          </>
        );

      case 'logic-condition':
        return (
          <>
            <div className="setting-field">
              <label>Condition Type</label>
              <select value={settings.conditionType || 'equals'} onChange={(e) => handleSettingChange('conditionType', e.target.value)}>
                <option value="equals">Equals</option>
                <option value="not-equals">Not Equals</option>
                <option value="contains">Contains</option>
                <option value="not-contains">Does Not Contain</option>
                <option value="greater-than">Greater Than</option>
                <option value="less-than">Less Than</option>
                <option value="exists">Field Exists</option>
                <option value="empty">Field is Empty</option>
                <option value="regex">Regex Match</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Field to Check</label>
              <input 
                type="text" 
                value={settings.field || ''} 
                onChange={(e) => handleSettingChange('field', e.target.value)} 
                placeholder="email, name, amount, status"
              />
              <small>Use dot notation for nested fields: user.email, order.total</small>
            </div>
            {!['exists', 'empty'].includes(settings.conditionType) && (
              <div className="setting-field">
                <label>Value to Compare</label>
                <input 
                  type="text" 
                  value={settings.value || ''} 
                  onChange={(e) => handleSettingChange('value', e.target.value)} 
                  placeholder="Value to compare against"
                />
              </div>
            )}
            <div className="setting-field">
              <label>Case Sensitive</label>
              <select value={settings.caseSensitive || 'false'} onChange={(e) => handleSettingChange('caseSensitive', e.target.value)}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </>
        );

      case 'action-database':
        return (
          <>
            <div className="setting-field">
              <label>Database Type</label>
              <select value={settings.dbType || 'mysql'} onChange={(e) => handleSettingChange('dbType', e.target.value)}>
                <option value="mysql">MySQL</option>
                <option value="postgresql">PostgreSQL</option>
                <option value="mongodb">MongoDB</option>
                <option value="sqlite">SQLite</option>
                <option value="firebase">Firebase</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Connection String</label>
              <input 
                type="password" 
                value={settings.connectionString || ''} 
                onChange={(e) => handleSettingChange('connectionString', e.target.value)} 
                placeholder="Database connection string"
              />
            </div>
            <div className="setting-field">
              <label>Operation</label>
              <select value={settings.operation || 'insert'} onChange={(e) => handleSettingChange('operation', e.target.value)}>
                <option value="insert">Insert Record</option>
                <option value="update">Update Record</option>
                <option value="delete">Delete Record</option>
                <option value="select">Select Records</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Table/Collection</label>
              <input 
                type="text" 
                value={settings.table || ''} 
                onChange={(e) => handleSettingChange('table', e.target.value)} 
                placeholder="table_name or collection_name"
              />
            </div>
            {(settings.operation === 'insert' || settings.operation === 'update') && (
              <div className="setting-field">
                <label>Data Fields</label>
                <textarea 
                  rows="4" 
                  value={settings.dataFields || ''} 
                  onChange={(e) => handleSettingChange('dataFields', e.target.value)} 
                  placeholder='{"name": "{{data.name}}", "email": "{{data.email}}"}'
                />
                <small>JSON format with variable substitution</small>
              </div>
            )}
            {(settings.operation === 'update' || settings.operation === 'delete' || settings.operation === 'select') && (
              <div className="setting-field">
                <label>Where Condition</label>
                <input 
                  type="text" 
                  value={settings.whereCondition || ''} 
                  onChange={(e) => handleSettingChange('whereCondition', e.target.value)} 
                  placeholder="id = {{data.id}} OR email = '{{data.email}}'"
                />
              </div>
            )}
          </>
        );

      case 'action-webhook':
        return (
          <>
            <div className="setting-field">
              <label>Webhook URL</label>
              <input 
                type="url" 
                value={settings.webhookUrl || ''} 
                onChange={(e) => handleSettingChange('webhookUrl', e.target.value)} 
                placeholder="https://api.example.com/webhook"
              />
            </div>
            <div className="setting-field">
              <label>HTTP Method</label>
              <select value={settings.method || 'POST'} onChange={(e) => handleSettingChange('method', e.target.value)}>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="setting-field">
              <label>Headers</label>
              <textarea 
                rows="3" 
                value={settings.headers || ''} 
                onChange={(e) => handleSettingChange('headers', e.target.value)} 
                placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
              />
              <small>JSON format</small>
            </div>
            <div className="setting-field">
              <label>Body</label>
              <textarea 
                rows="4" 
                value={settings.body || ''} 
                onChange={(e) => handleSettingChange('body', e.target.value)} 
                placeholder='{"data": "{{triggerData}}", "timestamp": "{{timestamp}}"}'
              />
            </div>
            <div className="setting-field">
              <label>Timeout (seconds)</label>
              <input 
                type="number" 
                value={settings.timeout || 30} 
                onChange={(e) => handleSettingChange('timeout', parseInt(e.target.value))} 
                min="1" 
                max="300"
              />
            </div>
          </>
        );

      default:
        return (
          <div className="setting-field">
            <label>Configuration</label>
            <textarea 
              rows="4" 
              value={settings.config || ''} 
              onChange={(e) => handleSettingChange('config', e.target.value)} 
              placeholder="Enter node configuration as JSON..."
            />
          </div>
        );
    }
  };

  const renderConfigurationTab = () => (
    <div className="tab-content">
      {renderConnectionStatus()}
      <div className="settings-fields">
        {renderNodeSpecificSettings()}
      </div>
    </div>
  );

  const renderTestingTab = () => (
    <div className="tab-content">
      <div className="testing-section">
        <h4>Test Configuration</h4>
        <div className="test-controls">
          <button className="btn primary">
            <FaPlay /> Run Test
          </button>
          <button className="btn secondary">
            <FaSync /> Validate Settings
          </button>
        </div>
        <div className="test-results">
          <h5>Test Results</h5>
          <div className="result-placeholder">
            Click "Run Test" to validate your configuration
          </div>
        </div>
      </div>
    </div>
  );

  const renderLogsTab = () => (
    <div className="tab-content">
      <div className="logs-section">
        <h4>Execution History</h4>
        <div className="log-entry">
          <div className="log-timestamp">2024-01-15 09:30:45</div>
          <div className="log-status success">✅ Success</div>
          <div className="log-message">Node executed successfully</div>
        </div>
        <div className="log-entry">
          <div className="log-timestamp">2024-01-15 08:15:22</div>
          <div className="log-status error">❌ Error</div>
          <div className="log-message">Connection timeout after 30 seconds</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`selected-node-settings-panel ${isVisible ? 'visible' : ''}`}>
      <div className="panel-header">
        <h3>
          <FaCog />
          {node.data?.label || node.data?.name || 'Node Configuration'}
        </h3>
        <button className="close-btn" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      <div className="panel-tabs">
        {renderTabButton('config', 'Configuration', <FaCog />)}
        {renderTabButton('test', 'Test', <FaPlay />)}
        {renderTabButton('logs', 'Logs', <FaFileAlt />)}
      </div>

      <div className="panel-content">
        {activeTab === 'config' && renderConfigurationTab()}
        {activeTab === 'test' && renderTestingTab()}
        {activeTab === 'logs' && renderLogsTab()}
      </div>

      <div className="action-buttons">
        <button className="btn secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" onClick={handleSave}>
          <FaSave />
          Save Configuration
        </button>
      </div>
    </div>
  );
};

export default NodeSettingsPanel;